import React, { useState } from 'react';
import { ChevronDown, Clock, Minus, Plus, RotateCcw, ShieldCheck } from 'lucide-react';
import { RobbieMark } from '../../ui';
import { AwardAllocation, QuoteComponentContext, QuoteComponentRecommendation } from '../../../types/quote.types';
import AllocationSplitBar from './AllocationSplitBar';
import { formatUnitPrice } from './quoteStyles';

interface ComponentAwardBuilderProps {
    component: QuoteComponentContext;
    aiRecommendation?: QuoteComponentRecommendation;
    allocations: AwardAllocation[];
    onChangeShare: (supplierName: string, newShare: number) => void;
    onResetToAI?: () => void;
    index: number;
    disabled: boolean;
    /** Viewer on someone else's RFQ: the split reads, the steppers go. */
    readOnly?: boolean;
}

const SHARE_STEP = 5;

/**
 * Per-component award editor: every shortlisted supplier gets a share stepper,
 * pre-filled from Robbie's recommendation. The AI's reasoning stays available
 * behind a collapsed toggle so the card itself remains scannable. A viewer
 * reads the same split without the controls that would change it.
 */
const ComponentAwardBuilder: React.FC<ComponentAwardBuilderProps> = ({
    component, aiRecommendation, allocations, onChangeShare, onResetToAI, index, disabled, readOnly = false
}) => {
    const [showReasoning, setShowReasoning] = useState(false);

    const shareFor = (supplierName: string) =>
        allocations.find(a => a.supplierName === supplierName)?.sharePercent || 0;
    const aiShareFor = (supplierName: string) =>
        aiRecommendation?.allocations.find(a => a.supplierName === supplierName)?.sharePercent;

    const activeAllocations = allocations.filter(a => a.sharePercent > 0);
    const total = activeAllocations.reduce((sum, a) => sum + a.sharePercent, 0);
    const isComplete = total === 100;
    const matchesAI = Boolean(aiRecommendation) && component.candidates.every(c =>
        (aiShareFor(c.supplierName) || 0) === shareFor(c.supplierName)
    );

    return (
        <div className="overflow-hidden rounded-card border border-line bg-surface">
            <div className="flex items-center justify-between gap-s4 border-b border-line-divider-soft px-s6 py-[10px]">
                <div className="flex min-w-0 items-center gap-s3">
                    <span className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[7px] border border-action-border bg-action-bg font-mono text-mono-data font-semibold text-action-deep">
                        {index + 1}
                    </span>
                    <div className="min-w-0">
                        <h3 className="m-0 truncate text-row-title text-ink">{component.name}</h3>
                        <div className="text-meta text-ink-muted">
                            {component.quantity} units{component.targetPrice ? ` · target ${formatUnitPrice(component.targetPrice, component.currency)}` : ''}
                        </div>
                    </div>
                </div>
                <span
                    className={`shrink-0 rounded-[4px] px-[7px] py-[1px] text-[10.5px] font-semibold ${
                        isComplete ? 'bg-success-bg text-success-ink' : 'bg-warn-bg text-warn-ink'
                    }`}
                >
                    {total}% {isComplete ? 'allocated' : 'of 100%'}
                </span>
            </div>

            <div className="flex flex-col gap-s4 px-s6 py-s4">
                {activeAllocations.length > 0 && <AllocationSplitBar allocations={activeAllocations} />}

                <div className="flex flex-col gap-s2">
                    {component.candidates.map(candidate => {
                        const share = shareFor(candidate.supplierName);
                        const aiShare = aiShareFor(candidate.supplierName);
                        return (
                            <div
                                key={candidate.supplierName}
                                className={`flex items-center gap-s4 rounded-[9px] border px-s4 py-s3 ${
                                    share > 0 ? 'border-action-border bg-action-bg' : 'border-line bg-surface'
                                }`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-s2">
                                        <span className={`truncate text-row-title ${share > 0 ? 'text-ink' : 'text-ink-secondary'}`}>
                                            {candidate.supplierName}
                                        </span>
                                        {aiShare !== undefined && (
                                            <span className="inline-flex shrink-0 items-center gap-[3px] rounded-[4px] bg-ai-chip px-[6px] py-[1px] text-[10.5px] font-semibold text-robbie">
                                                <RobbieMark size="xxs" aria-hidden />
                                                {aiShare}%
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-[2px] flex flex-wrap items-center gap-x-s3 gap-y-[2px] text-meta text-ink-secondary">
                                        <span className="font-mono text-mono-data">{formatUnitPrice(candidate.quotedPrice, candidate.currency)}</span>
                                        {candidate.quotedPrice != null && (
                                            candidate.quoteSource === 'SUPPLIER_QUOTED' ? (
                                                <span className="rounded-[4px] bg-success-bg px-[6px] py-[1px] text-[10.5px] font-semibold text-success-ink">
                                                    quoted
                                                </span>
                                            ) : (
                                                <span
                                                    className="rounded-[4px] bg-neutral-bg px-[6px] py-[1px] text-[10.5px] font-semibold text-neutral-ink"
                                                    title="Research estimate — this price was scraped, not offered by the supplier"
                                                >
                                                    estimate
                                                </span>
                                            )
                                        )}
                                        {candidate.leadTimeWeeks != null && (
                                            <span className="flex items-center gap-[3px]">
                                                <Clock className="h-[11px] w-[11px] text-ink-muted" />
                                                {candidate.leadTimeWeeks}w
                                            </span>
                                        )}
                                        {candidate.trustScore != null && (
                                            <span className="flex items-center gap-[3px]">
                                                <ShieldCheck className="h-[11px] w-[11px] text-ink-muted" />
                                                {candidate.trustScore}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex shrink-0 items-center gap-s1">
                                    {!readOnly && (
                                    <button
                                        type="button"
                                        onClick={() => onChangeShare(candidate.supplierName, Math.max(0, share - SHARE_STEP))}
                                        disabled={disabled || share === 0}
                                        aria-label={`Decrease ${candidate.supplierName} share`}
                                        className="flex h-[26px] w-[26px] items-center justify-center rounded-control border border-line bg-surface text-ink-secondary transition-colors hover:border-line-strong hover:text-ink disabled:opacity-30"
                                    >
                                        <Minus className="h-[13px] w-[13px]" />
                                    </button>
                                    )}
                                    <span className={`w-[44px] text-center font-mono text-mono-data font-semibold ${share > 0 ? 'text-action-deep' : 'text-ink-muted'}`}>
                                        {share}%
                                    </span>
                                    {!readOnly && (
                                    <button
                                        type="button"
                                        onClick={() => onChangeShare(candidate.supplierName, Math.min(100, share + SHARE_STEP))}
                                        disabled={disabled || share === 100}
                                        aria-label={`Increase ${candidate.supplierName} share`}
                                        className="flex h-[26px] w-[26px] items-center justify-center rounded-control border border-line bg-surface text-ink-secondary transition-colors hover:border-line-strong hover:text-ink disabled:opacity-30"
                                    >
                                        <Plus className="h-[13px] w-[13px]" />
                                    </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center justify-between">
                    {aiRecommendation ? (
                        <button
                            type="button"
                            onClick={() => setShowReasoning(v => !v)}
                            className="flex items-center gap-s1 text-meta font-semibold text-robbie hover:text-robbie-ink"
                        >
                            Why Robbie suggests this
                            <ChevronDown className={`h-[13px] w-[13px] transition-transform ${showReasoning ? 'rotate-180' : ''}`} />
                        </button>
                    ) : <span />}

                    {aiRecommendation && !matchesAI && onResetToAI && !readOnly && (
                        <button
                            type="button"
                            onClick={onResetToAI}
                            disabled={disabled}
                            className="flex items-center gap-s1 text-meta font-semibold text-neutral-ink hover:text-ink disabled:opacity-40"
                        >
                            <RotateCcw className="h-[11px] w-[11px]" />
                            Reset to Robbie's plan
                        </button>
                    )}
                </div>

                {showReasoning && aiRecommendation && (
                    <div className="flex flex-col gap-s2 rounded-control border border-robbie-border-soft bg-ai-chip px-s4 py-s4 text-body-sm text-ink-body">
                        <p className="m-0">{aiRecommendation.reasoning}</p>
                        {aiRecommendation.negotiationTip && (
                            <p className="m-0 text-warn-ink">
                                <span className="font-semibold">Negotiation tip:</span> {aiRecommendation.negotiationTip}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ComponentAwardBuilder;
